(() => {
  const current = window.HumanPrimeSmartHeader;
  if (current) {
    current.scan(document);
    return;
  }

  const instances = new WeakMap();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const initialize = (header) => {
    if (!header || instances.has(header)) return;
    const owner = header.closest('.shopify-section') || header.parentElement;
    const drawer = owner?.querySelector('[data-smart-mobile-menu]');
    const opener = header.querySelector('[data-smart-menu-open]');
    const panel = drawer?.querySelector('[role="dialog"]');
    const controller = new AbortController();
    const { signal } = controller;
    let scrollFrame = 0;
    let closeTimer = 0;
    let cartTimer = 0;
    let backgroundState = [];

    const updateHeader = () => {
      scrollFrame = 0;
      header.classList.toggle('is-compact', window.scrollY > 24);
    };
    const onScroll = () => {
      if (!scrollFrame) scrollFrame = requestAnimationFrame(updateHeader);
    };

    const focusable = () => Array.from(panel?.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])') || [])
      .filter((element) => element.getClientRects().length > 0);

    const makeBackgroundInert = () => {
      const targets = Array.from(document.body.children).filter((element) => element !== owner);
      backgroundState = targets.map((element) => ({ element, inert: element.inert, ariaHidden: element.getAttribute('aria-hidden') }));
      targets.forEach((element) => {
        element.inert = true;
        element.setAttribute('aria-hidden', 'true');
      });
      header.inert = true;
    };

    const restoreBackground = () => {
      backgroundState.forEach(({ element, inert, ariaHidden }) => {
        element.inert = inert;
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      });
      backgroundState = [];
      header.inert = false;
    };

    const closeMenu = ({ restoreFocus = true, immediate = false } = {}) => {
      if (!drawer || drawer.hidden) return;
      window.clearTimeout(closeTimer);
      drawer.classList.remove('is-open');
      opener?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('hp-mobile-menu-open');
      restoreBackground();
      const finish = () => {
        drawer.hidden = true;
        if (restoreFocus) opener?.focus({ preventScroll: true });
      };
      if (immediate || reduceMotion.matches) finish();
      else closeTimer = window.setTimeout(finish, 220);
    };

    const openMenu = () => {
      if (!drawer || !panel) return;
      window.clearTimeout(closeTimer);
      drawer.hidden = false;
      opener?.setAttribute('aria-expanded', 'true');
      makeBackgroundInert();
      document.body.classList.add('hp-mobile-menu-open');
      requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        drawer.querySelector('[data-smart-menu-close]:not(.hp-mobile-menu__backdrop)')?.focus({ preventScroll: true });
      });
    };

    const updateCart = (event) => {
      const count = Number(event.detail?.cart?.item_count);
      if (!Number.isFinite(count)) return;
      owner.querySelectorAll('[data-cart-count-display]').forEach((node) => {
        node.textContent = String(count);
        if (node.classList.contains('site-header__cart-count')) node.hidden = count === 0;
      });
      const live = header.querySelector('[data-cart-count-live]');
      if (live) live.textContent = `${count} ${count === 1 ? 'producto' : 'productos'} en el carrito`;
      const badge = header.querySelector('.site-header__cart-count');
      if (badge) {
        badge.classList.remove('is-updated');
        requestAnimationFrame(() => badge.classList.add('is-updated'));
        window.clearTimeout(cartTimer);
        cartTimer = window.setTimeout(() => badge.classList.remove('is-updated'), 400);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true, signal });
    document.addEventListener('cart:updated', updateCart, { signal });
    opener?.addEventListener('click', () => openMenu(), { signal });
    drawer?.querySelectorAll('[data-smart-menu-close]').forEach((button) => button.addEventListener('click', () => closeMenu(), { signal }));
    drawer?.querySelectorAll('.hp-mobile-menu__nav a').forEach((link) => link.addEventListener('click', () => closeMenu({ restoreFocus: false, immediate: true }), { signal }));
    drawer?.querySelector('[data-premium-search-open]')?.addEventListener('click', () => closeMenu({ restoreFocus: false, immediate: true }), { capture: true, signal });

    drawer?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeMenu();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }, { signal });

    updateHeader();
    instances.set(header, {
      destroy() {
        controller.abort();
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
        window.clearTimeout(closeTimer);
        window.clearTimeout(cartTimer);
        closeMenu({ restoreFocus: false, immediate: true });
      }
    });
  };

  const destroy = (header) => {
    const instance = instances.get(header);
    if (!instance) return;
    instance.destroy();
    instances.delete(header);
  };

  const scan = (scope) => {
    if (scope.matches?.('[data-smart-header]')) initialize(scope);
    scope.querySelectorAll?.('[data-smart-header]').forEach(initialize);
  };

  window.HumanPrimeSmartHeader = { scan, destroy };
  scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const header = event.target.matches?.('[data-smart-header]') ? event.target : event.target.querySelector?.('[data-smart-header]');
    destroy(header);
  });
})();
